import { useState, useEffect } from 'react';
import { Gift, Settings, Star, ArrowRight } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { toast } from 'sonner';

interface LoyaltyConfig {
  valor_por_ponto: number;
  minimo_resgate: number;
  valor_desconto_por_ponto: number;
  ativo: boolean;
}

export function LoyaltyPage() {
  const [config, setConfig] = useState<LoyaltyConfig>({
    valor_por_ponto: 10,
    minimo_resgate: 100,
    valor_desconto_por_ponto: 0.1,
    ativo: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function loadConfig() {
      try {
        const res = await apiFetch('/api/loyalty/config');
        if (res.ok) {
          const data = await res.json();
          setConfig(data);
        }
      } catch {
        // Use defaults
      }
    }
    loadConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiFetch('/api/loyalty/config', {
        method: 'POST',
        body: JSON.stringify(config),
      });
      if (res.ok) {
        toast.success('Configuração salva com sucesso!');
      } else {
        const err = await res.json();
        toast.error(err.error || 'Erro ao salvar configuração');
      }
    } catch {
      toast.error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Programa de Fidelidade</h1>
        <p className="text-sm text-muted-foreground">
          Configure o programa de pontos para seus clientes
        </p>
      </div>

      {/* Config Card */}
      <div className="rounded-xl border border-border bg-card/50 backdrop-blur-sm p-6 max-w-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="rounded-lg bg-primary/10 p-2">
            <Settings className="h-5 w-5 text-primary" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">Configuração</h2>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm text-foreground">Programa Ativo</label>
            <button
              onClick={() => setConfig({ ...config, ativo: !config.ativo })}
              className={`relative h-6 w-11 rounded-full transition-colors ${
                config.ativo ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                  config.ativo ? 'translate-x-5' : ''
                }`}
              />
            </button>
          </div>

          <div>
            <label className="text-sm text-muted-foreground">Valor por ponto (R$)</label>
            <p className="text-xs text-muted-foreground mb-1">
              A cada R$ X gastos, o cliente ganha 1 ponto
            </p>
            <input
              type="number"
              value={config.valor_por_ponto}
              onChange={(e) => setConfig({ ...config, valor_por_ponto: Number(e.target.value) })}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              min={1}
              step={1}
            />
          </div>

          <div>
            <label className="text-sm text-muted-foreground">Mínimo para resgate (pontos)</label>
            <input
              type="number"
              value={config.minimo_resgate}
              onChange={(e) => setConfig({ ...config, minimo_resgate: Number(e.target.value) })}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              min={1}
            />
          </div>

          <div>
            <label className="text-sm text-muted-foreground">Valor do desconto por ponto (R$)</label>
            <input
              type="number"
              value={config.valor_desconto_por_ponto}
              onChange={(e) =>
                setConfig({ ...config, valor_desconto_por_ponto: Number(e.target.value) })
              }
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              min={0.01}
              step={0.01}
            />
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? 'Salvando...' : 'Salvar Configuração'}
          </button>
        </div>
      </div>

      {/* How it works */}
      <div className="rounded-xl border border-border bg-card/50 backdrop-blur-sm p-6 max-w-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="rounded-lg bg-amber-500/10 p-2">
            <Gift className="h-5 w-5 text-amber-500" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">Como funciona</h2>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-bold">
              1
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground">
                Cliente gasta R${config.valor_por_ponto.toFixed(2)}
              </span>
              <ArrowRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-sm text-foreground">Ganha 1 ponto</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-bold">
              2
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground">
                Acumula {config.minimo_resgate} pontos
              </span>
              <ArrowRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-sm text-foreground">Pode resgatar</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-bold">
              3
            </div>
            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 text-amber-500" />
              <span className="text-sm text-foreground">
                Cada ponto = R${config.valor_desconto_por_ponto.toFixed(2)} de desconto
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
